import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import ArrowRightCircleIcon from "@/assets/icons/arrowCircle/right/regular.svg?react"
import ArrowRightNarrowIcon from "@/assets/icons/arrowNarrow/right/regular.svg?react"
import CheckCircleFilledIcon from "@/assets/icons/checkCircle/filled/regular.svg?react"
import { Button } from "@/components/ds/Button"
import { Typography } from "@/components/ds/Typography"
import { Skeleton } from "@/components/ui/skeleton"
import { useScope1Blocked } from "@/hooks/useScope1Blocked"
import { cn } from "@/lib/utils"
import routes from "@/routes/routes"
import { type SetupJourneyStepKey, useGetOnboardingSetupJourneyQuery } from "@/store/onboardingAPI"

// ─── Step item ────────────────────────────────────────────────────────────────

interface StepItemProps {
  complete: boolean
  locked: boolean
  label: string
  description: string
  href: string
  buttonLabel: string
  isLast: boolean
}

function StepItem({ complete, locked, label, description, href, buttonLabel, isLast }: StepItemProps) {
  if (complete) {
    return (
      <div
        className={cn(
          "flex items-center gap-2.5 bg-safe-mainBg px-6 py-5",
          !isLast && "border-b border-neutral-border",
        )}
      >
        <CheckCircleFilledIcon className="h-6 w-6 shrink-0 text-safe-primary" />
        <div className="flex flex-col gap-0.5">
          <Typography variant="body1" weight="medium" className="text-safe-primary line-through">
            {label}
          </Typography>
          <Typography variant="body2" className="text-safe-primary line-through">
            {description}
          </Typography>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn("flex items-center justify-between gap-4 px-6 py-5", !isLast && "border-b border-neutral-border")}
    >
      <div className="flex items-center gap-2.5">
        <ArrowRightCircleIcon className="h-6 w-6 shrink-0 text-neutral-text" />
        <div className="flex flex-col gap-0.5">
          <Typography variant="body1" weight="medium" className="text-neutral-title">
            {label}
          </Typography>
          <Typography variant="body2" className="text-neutral-text">
            {description}
          </Typography>
        </div>
      </div>
      <div className="shrink-0">
        {locked ? (
          <Button variant="primary" size="small" disabled className="gap-1.5 whitespace-nowrap">
            {buttonLabel}
            <ArrowRightNarrowIcon className="h-5 w-5 text-white" />
          </Button>
        ) : (
          <Link to={href}>
            <Button variant="primary" size="small" className="gap-1.5 whitespace-nowrap">
              {buttonLabel}
              <ArrowRightNarrowIcon className="h-5 w-5 text-white" />
            </Button>
          </Link>
        )}
      </div>
    </div>
  )
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function Scope1BlockedSkeleton() {
  return (
    <div className="flex flex-1 items-center justify-center bg-neutral-secondBg px-6 py-10">
      <div className="mx-auto w-full max-w-2xl">
        <div className="mb-8 flex flex-col gap-3">
          <Skeleton className="h-11 w-64 rounded-lg bg-neutral-border" />
          <Skeleton className="h-7 w-96 rounded-lg bg-neutral-border" />
        </div>
        <div className="rounded-2xl shadow-shadow-bottom-md">
          <div className="overflow-hidden rounded-2xl bg-neutral-mainBg">
            <div className="px-6 py-5">
              <Skeleton className="h-6 w-72 rounded bg-neutral-border" />
            </div>
            <div className="h-1 bg-neutral-border" />
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between border-b border-neutral-border px-6 py-5">
                <div className="flex items-center gap-2.5">
                  <Skeleton className="h-6 w-6 rounded-full bg-neutral-border" />
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-4 w-48 rounded bg-neutral-border" />
                    <Skeleton className="h-3 w-72 rounded bg-neutral-border" />
                  </div>
                </div>
                <Skeleton className="h-9 w-32 rounded-full bg-neutral-border" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export const Scope1Blocked = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: journey, isLoading, isError, refetch } = useGetOnboardingSetupJourneyQuery()
  const { acknowledge } = useScope1Blocked()

  if (isError)
    return (
      <div role="alert" className="p-8">
        <p>{t("organizationSetup.climateError")}</p>
        <Button onClick={() => refetch()}>{t("organizationSetup.retry")}</Button>
        <Link to={routes["dashboard/campaign-manager"]}>{t("advertising")}</Link>
      </div>
    )
  if (isLoading || !journey) return <Scope1BlockedSkeleton />

  // Map each BE step key to its frontend route and i18n labels
  const stepConfig: Record<
    SetupJourneyStepKey,
    { label: string; description: string; href: string; buttonLabel: string }
  > = {
    PARENT_COMPANY: {
      label: t("scope1Blocked.steps.company.label"),
      description: t("scope1Blocked.steps.company.description"),
      href: routes["dashboard/company"],
      buttonLabel: t("scope1Blocked.steps.company.button"),
    },
    REPORTING_YEAR: {
      label: t("scope1Blocked.steps.reportingYear.label"),
      description: t("scope1Blocked.steps.reportingYear.description"),
      href: routes["dashboard/company/reporting-year"],
      buttonLabel: t("scope1Blocked.steps.reportingYear.button"),
    },
    RELATED_ENTITIES: {
      label: t("scope1Blocked.steps.relatedEntities.label"),
      description: t("scope1Blocked.steps.relatedEntities.description"),
      href: routes["dashboard/company/relatedEntities"],
      buttonLabel: t("scope1Blocked.steps.relatedEntities.button"),
    },
    LOCATIONS: {
      label: t("scope1Blocked.steps.locations.label"),
      description: t("scope1Blocked.steps.locations.description"),
      href: routes["dashboard/company/locations"],
      buttonLabel: t("scope1Blocked.steps.locations.button"),
    },
  }

  // Sorted by order ascending, just in case the API sends them out of order
  const sortedSteps = [...journey.steps].sort((a, b) => a.order - b.order)

  // Progress: account step is always complete (1), plus completed BE steps.
  // Total: 1 hardcoded + all BE steps. Minimum 1 % so the bar is always visible.
  const totalSteps = 1 + sortedSteps.length
  const completedSteps = 1 + sortedSteps.filter((s) => s.status === "COMPLETED").length
  const progressPct = Math.max((completedSteps / totalSteps) * 100, 1)
  const allComplete = journey.setupComplete

  return (
    <div className="flex flex-1 items-center justify-center bg-neutral-secondBg px-6 py-10">
      <div className="w-full max-w-2xl">
        {/* Page title + subtitle */}
        <div className="mb-8 flex flex-col gap-2">
          <Typography variant="title" weight="medium" className="text-neutral-title">
            {t("organizationSetup.climateTitle")}
          </Typography>
          <Typography variant="heading" className="text-neutral-text">
            {t("organizationSetup.climateDescription")}
          </Typography>
        </div>

        {/* Outer wrapper owns the shadow; inner clips children to rounded corners */}
        <div className="rounded-2xl shadow-shadow-bottom-md">
          <div className="overflow-hidden rounded-2xl bg-neutral-mainBg">
            {/* Card heading row */}
            <div className="flex items-center gap-2.5 px-6 py-5">
              <Typography variant="subtitle" weight="medium" className="text-neutral-title">
                {t("scope1Blocked.cardTitle")}
              </Typography>
            </div>

            {/* Progress divider: width reflects completed steps, min 1% */}
            <div className="relative h-1 bg-neutral-border">
              <div
                className="absolute left-0 top-0 h-full bg-brand-primary transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            {/* Step 1 — account created (hardcoded, always complete) */}
            <StepItem
              complete
              locked={false}
              label={t("scope1Blocked.steps.account.label")}
              description={t("scope1Blocked.steps.account.description")}
              href={routes.dashboard}
              buttonLabel=""
              isLast={sortedSteps.length === 0}
            />

            {/* Steps 2-N — driven by BE journey */}
            {sortedSteps.map((step, index) => {
              const config = stepConfig[step.key]
              return (
                <StepItem
                  key={step.key}
                  complete={step.status === "COMPLETED"}
                  locked={step.status === "LOCKED"}
                  label={config.label}
                  description={config.description}
                  href={config.href}
                  buttonLabel={config.buttonLabel}
                  isLast={index === sortedSteps.length - 1}
                />
              )
            })}
          </div>
        </div>

        {/* All steps done: let the user proceed to the Overview dashboard */}
        {allComplete && (
          <div className="mt-6 flex justify-end">
            <Button
              variant="primary"
              size="medium"
              className="gap-2"
              onClick={() => {
                acknowledge()
                navigate(routes.climate)
              }}
            >
              {t("scope1Blocked.goToOverview")}
              <ArrowRightNarrowIcon className="h-5 w-5 text-white" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
